import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Modal,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useServiceOrder,
  useAddServiceOrderMaterial,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
} from './useServiceOrders';
import { ServiceOrderPicker } from './ServiceOrderPicker';
import { formatCurrency, formatDate } from '../../shared/utils/format';

interface ServiceOrderMaterialsPageProps {
  /** Id da OS. Se ausente, le `?os=` da URL; sem isso, mostra o seletor. */
  orderId?: string;
}

/**
 * Materiais e pecas consumidos numa Ordem de Servico.
 *
 * Consome:
 *   GET  /manufacturing/service-orders/:id  (agregado — lista de materials)
 *   POST /manufacturing/service-orders/:id/materials
 *        body: { productId, description?, quantity, unit, unitCost, usedAt? }
 *        resp: { materialId, totalCost, totalMaterialCost }
 */
export function ServiceOrderMaterialsPage({
  orderId,
}: ServiceOrderMaterialsPageProps): JSX.Element {
  const { addToast } = useToast();

  const urlOrderId = useMemo(
    () => new URLSearchParams(window.location.search).get('os') ?? undefined,
    [],
  );
  const [pickedId, setPickedId] = useState<string | null>(null);
  const activeId = orderId ?? pickedId ?? urlOrderId;

  const { data: detail, isLoading, error, refetch } = useServiceOrder(activeId);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    productId: '',
    description: '',
    quantity: '',
    unit: 'UN',
    unitCost: '',
    usedAt: '',
  });
  const addMaterial = useAddServiceOrderMaterial();

  const materials = detail?.materials ?? [];
  const sumCost = useMemo(() => materials.reduce((a, m) => a + m.totalCost, 0), [materials]);

  if (!activeId) {
    return (
      <div className="so-page">
        <ServiceOrderPicker onSelect={setPickedId} label="Materiais e Peças" />
      </div>
    );
  }

  const resetForm = (): void => {
    setForm({ productId: '', description: '', quantity: '', unit: 'UN', unitCost: '', usedAt: '' });
  };

  const handleSubmit = async (): Promise<void> => {
    const quantity = Number(form.quantity);
    const unitCost = Number(form.unitCost);
    if (!form.productId.trim() || !form.unit.trim() || !form.quantity || !Number.isFinite(quantity) || quantity <= 0) {
      addToast('Informe o produto, a quantidade e a unidade de medida', 'error');
      return;
    }
    if (!form.unitCost || !Number.isFinite(unitCost) || unitCost < 0) {
      addToast('Informe um custo unitário válido', 'error');
      return;
    }
    try {
      const result = await addMaterial.mutateAsync({
        id: activeId,
        productId: form.productId.trim(),
        description: form.description.trim() || undefined,
        quantity,
        unit: form.unit.trim(),
        unitCost,
        usedAt: form.usedAt ? new Date(form.usedAt).toISOString() : undefined,
      });
      addToast(
        `Material registrado. Custo total da OS: ${formatCurrency(result.totalMaterialCost)}`,
        'success',
      );
      setModalOpen(false);
      resetForm();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao registrar material', 'error');
    }
  };

  return (
    <div className="so-page">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <div className="flex items-center gap-sm mb-xs flex-wrap">
            <span
              className="font-label-md text-label-md px-2 py-1 rounded"
              style={{ background: '#eceef0', color: '#43474e' }}
            >
              {detail?.number ?? 'Carregando...'}
            </span>
            {detail && (
              <Badge variant={SERVICE_ORDER_STATUS_VARIANT[detail.status]} size="sm">
                {SERVICE_ORDER_STATUS_LABEL[detail.status]}
              </Badge>
            )}
          </div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Materiais e Peças</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            {detail?.title ? `${detail.number}: ${detail.title}` : 'Materiais e peças consumidos na OS.'}
          </p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }}>+ Adicionar Material</Button>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-md mb-lg">
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">inventory_2</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Custo total da OS</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">
            {detail ? formatCurrency(detail.totalMaterialCost) : '—'}
          </div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Soma oficial (servidor)</p>
        </div>
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">build</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Itens</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{materials.length}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Linhas nesta OS</p>
        </div>
        <div className="so-kpi">
          <div className="so-kpi__top">
            <span className="material-symbols-outlined" aria-hidden="true">summarize</span>
            <span className="font-label-md text-label-md text-on-surface-variant uppercase">Soma exibida</span>
          </div>
          <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{formatCurrency(sumCost)}</div>
          <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Soma dos itens listados</p>
        </div>
      </div>

      {/* ── Loading / Error / Empty / Table ── */}
      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="state-error" role="alert">
            <p>Erro ao carregar a ordem de serviço</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {detail && !isLoading && !error && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {materials.length} {materials.length === 1 ? 'item' : 'itens'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Materiais e peças utilizados</h3>
          </CardHeader>
          <div className="so-table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Descrição</th>
                  <th style={{ textAlign: 'right' }}>Qtd</th>
                  <th>Unid.</th>
                  <th style={{ textAlign: 'right' }}>Custo un.</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th>Usado em</th>
                </tr>
              </thead>
              <tbody>
                {materials.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="state-empty">
                        Nenhum material registrado ainda. Use “+ Adicionar Material” para registrar peças e insumos.
                      </div>
                    </td>
                  </tr>
                )}
                {materials.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{m.productId.slice(0, 8)}</td>
                    <td>{m.description ?? '—'}</td>
                    <td className="so-num">{m.quantity}</td>
                    <td>{m.unit}</td>
                    <td className="so-num">{formatCurrency(m.unitCost)}</td>
                    <td className="so-num">{formatCurrency(m.totalCost)}</td>
                    <td>{formatDate(m.usedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {materials.length > 0 && (
            <div className="so-table-footer">
              <span className="so-page-info">Total: <strong>{formatCurrency(sumCost)}</strong></span>
            </div>
          )}
        </Card>
      )}

      {/* ── Modal de novo material ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Adicionar Material — ${detail?.number ?? ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} loading={addMaterial.isPending}>Adicionar material</Button>
          </>
        }
      >
        <div className="so-form">
          <Input
            label="Produto (ID) *"
            value={form.productId}
            onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))}
            placeholder="UUID do item (master_data/inventory)"
          />
          <Input
            label="Descrição"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Ex: Filtro de ar industrial X-200"
          />
          <div className="so-form__row">
            <Input
              label="Quantidade *"
              type="number"
              min="0.01"
              step="0.01"
              value={form.quantity}
              onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
            />
            <Input
              label="Unidade *"
              value={form.unit}
              onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
              placeholder="UN, CX, L..."
            />
          </div>
          <Input
            label="Custo unitário *"
            type="number"
            min="0"
            step="0.01"
            value={form.unitCost}
            onChange={(e) => setForm((p) => ({ ...p, unitCost: e.target.value }))}
          />
          <Input
            label="Data de uso"
            type="datetime-local"
            value={form.usedAt}
            onChange={(e) => setForm((p) => ({ ...p, usedAt: e.target.value }))}
          />
        </div>
      </Modal>

      <style>{`
        .so-page { padding: 1.5rem; }
        .so-kpi { background: #ffffff; border: 1px solid #c4c6cf; border-radius: 8px; padding: 1rem; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); }
        .so-kpi__top { display: flex; align-items: center; gap: 0.375rem; margin-bottom: 0.25rem; }
        .so-kpi__top .material-symbols-outlined { font-size: 18px; color: #43474e; }
        .so-table-wrap { overflow-x: auto; }
        .so-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .so-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600;
          font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .so-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; vertical-align: top; }
        .so-table tbody tr:hover td { background: #eceef0; }
        .so-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .so-table-footer { display: flex; justify-content: flex-end; padding: 0.75rem 1rem; border-top: 1px solid #eceef0; }
        .so-page-info { font-size: 0.8125rem; color: #43474e; }
        .so-form { display: flex; flex-direction: column; gap: 1rem; min-width: 380px; }
        .so-form__row { display: flex; gap: 0.75rem; }
        .so-form__row > * { flex: 1; }
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1.5rem 0.5rem; color: #74777f; }
      `}</style>
    </div>
  );
}